import React, { useState } from "react";
import type { League, LeagueSelectorProps } from "../types";
import bl1Logo from "../assets/bl1_logo.svg";
import bl2Logo from "../assets/bl2_logo.svg";
import plLogo from "../assets/pl_logo.png";

const LeagueSelector: React.FC<LeagueSelectorProps> = ({
  currentLeague,
  onLeagueChange,
  availableLeagues,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const formatSeasonLabel = (season: string): string => {
    const startYear = parseInt(season, 10);
    if (isNaN(startYear)) return season;
    const startShort = String(startYear % 100).padStart(2, "0");
    const endShort = String((startYear + 1) % 100).padStart(2, "0");
    return `${startShort}/${endShort}`;
  };

  // Prefer DB-provided logo_url; fall back to bundled assets for known codes;
  // tournaments without an asset render an emoji marker.
  const getLeagueLogo = (league: League): string | undefined => {
    if (league.logo_url) return league.logo_url;
    switch (league.code) {
      case "bl1":
        return bl1Logo;
      case "bl2":
        return bl2Logo;
      case "pl":
        return plLogo;
      default:
        return undefined;
    }
  };

  const getLeagueFallbackEmoji = (league: League): string => {
    if (league.type === "TOURNAMENT") return "🏆";
    if (league.country === "Deutschland") return "🇩🇪";
    return "⚽";
  };

  const getBaseName = (league: League): string => {
    const name = league.name;
    if (name.includes("1. Bundesliga") || name.includes("Bundesliga 1")) {
      return "1. Bundesliga";
    }
    if (name.includes("2. Bundesliga") || name.includes("Bundesliga 2")) {
      return "2. Bundesliga";
    }
    if (name.includes("Premier League")) {
      return "Premier League";
    }
    return name;
  };

  const getSeasonSuffix = (league: League): string => {
    if (league.season_label) return league.season_label;
    if (league.season) return formatSeasonLabel(league.season);
    return "";
  };

  const getLeagueDisplayName = (league: League): string => {
    const baseName = getBaseName(league);
    const suffix = getSeasonSuffix(league);
    if (!suffix) return baseName;
    // Tournament names often already contain the year (e.g. "FIFA WM 2026");
    // skip the suffix in that case to avoid "FIFA WM 2026 2026".
    if (league.type === "TOURNAMENT" && baseName.includes(suffix)) {
      return baseName;
    }
    return `${baseName} ${suffix}`;
  };

  const handleLeagueSelect = (league: League) => {
    onLeagueChange(league);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={toggleDropdown}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "12px 20px",
          background: "rgba(255, 255, 255, 0.95)",
          border: "1px solid rgba(102, 126, 234, 0.2)",
          borderRadius: "25px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          fontSize: "16px",
          fontWeight: "600",
          color: "#333",
          cursor: "pointer",
          transition: "all 0.2s ease",
          outline: "none",
          backdropFilter: "blur(10px)",
          fontFamily:
            '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          letterSpacing: "-0.01em",
        }}
        onMouseEnter={(e) => {
          const target = e.target as HTMLElement;
          target.style.background = "rgba(255, 255, 255, 1)";
          target.style.borderColor = "rgba(102, 126, 234, 0.4)";
          target.style.transform = "translateY(-1px)";
          target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)";
        }}
        onMouseLeave={(e) => {
          const target = e.target as HTMLElement;
          target.style.background = "rgba(255, 255, 255, 0.95)";
          target.style.borderColor = "rgba(102, 126, 234, 0.2)";
          target.style.transform = "translateY(0)";
          target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
        }}
      >
        {currentLeague && getLeagueLogo(currentLeague) ? (
          <img
            src={getLeagueLogo(currentLeague)}
            alt={getLeagueDisplayName(currentLeague)}
            style={{
              width: "24px",
              height: "24px",
            }}
          />
        ) : (
          <span style={{ fontSize: "20px" }}>
            {currentLeague ? getLeagueFallbackEmoji(currentLeague) : "⚽"}
          </span>
        )}
        <span
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#333",
            fontFamily:
              '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            letterSpacing: "-0.01em",
          }}
        >
          {currentLeague
            ? getLeagueDisplayName(currentLeague)
            : "Liga auswählen"}
        </span>
        <svg
          style={{
            width: "18px",
            height: "18px",
            color: "rgba(102, 126, 234, 0.7)",
            transition: "transform 0.2s ease",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            right: "0",
            top: "calc(100% + 8px)",
            minWidth: "220px",
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(15px)",
            border: "1px solid rgba(102, 126, 234, 0.2)",
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
            zIndex: 1000,
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "8px" }}>
            {availableLeagues.map((league) => (
              <button
                key={league.id}
                type="button"
                onClick={() => handleLeagueSelect(league)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "14px 16px",
                  fontSize: "16px",
                  background:
                    league.id === currentLeague?.id
                      ? "rgba(102, 126, 234, 0.1)"
                      : "transparent",
                  color:
                    league.id === currentLeague?.id
                      ? "rgba(102, 126, 234, 1)"
                      : "#333",
                  fontWeight: league.id === currentLeague?.id ? "700" : "600",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontFamily:
                    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  letterSpacing: "-0.01em",
                }}
                onMouseEnter={(e) => {
                  const target = e.target as HTMLElement;
                  if (league.id !== currentLeague?.id) {
                    target.style.background = "rgba(102, 126, 234, 0.05)";
                  }
                }}
                onMouseLeave={(e) => {
                  const target = e.target as HTMLElement;
                  if (league.id !== currentLeague?.id) {
                    target.style.background = "transparent";
                  }
                }}
              >
                {getLeagueLogo(league) ? (
                  <img
                    src={getLeagueLogo(league)}
                    alt={getLeagueDisplayName(league)}
                    style={{
                      width: "20px",
                      height: "20px",
                    }}
                  />
                ) : (
                  <span style={{ fontSize: "20px" }}>
                    {getLeagueFallbackEmoji(league)}
                  </span>
                )}
                <span>{getLeagueDisplayName(league)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Backdrop to close dropdown */}
      {isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default LeagueSelector;
